import styled, { css } from 'styled-components';

// Overlay layer spanning the whole scrollable profile card. Anchored to
// [data-site-container] (DesignSiteContainer is position:relative). It never
// captures pointer events itself — only the stickers do, and only in edit mode —
// so blocks underneath stay clickable on the live site and scrolling works.
export const StickerLayerRoot = styled.div`
  position: absolute;
  inset: 0;
  z-index: 50;
  pointer-events: none;
  overflow: visible;
`;

const HANDLE_POS = {
  tl: 'top: -7px; left: -7px; cursor: nwse-resize;',
  tr: 'top: -7px; right: -7px; cursor: nesw-resize;',
  bl: 'bottom: -7px; left: -7px; cursor: nesw-resize;',
  br: 'bottom: -7px; right: -7px; cursor: nwse-resize;',
};

export const StickerBox = styled.div`
  position: absolute;
  user-select: none;
  -webkit-user-select: none;
  will-change: transform;

  ${(props) =>
    props.$editable
      ? css`
          pointer-events: auto;
          cursor: grab;
          touch-action: none;
          &:active {
            cursor: grabbing;
          }
        `
      : props.$linked
        ? css`
            pointer-events: auto;
            cursor: pointer;
          `
        : css`
            pointer-events: none;
          `}
`;

export const StickerContent = styled.div`
  width: 100%;
  line-height: 1;
  text-align: center;
  display: block;

  & > svg,
  & > img,
  & > span {
    display: block;
    width: 100%;
    height: auto;
  }
`;

export const StickerOutline = styled.div`
  position: absolute;
  inset: -6px;
  border: 1.5px dashed #6366f1;
  border-radius: 6px;
  pointer-events: none;
`;

export const StickerHandle = styled.div`
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #6366f1;
  pointer-events: auto;
  touch-action: none;
  ${(props) => HANDLE_POS[props.$pos] || HANDLE_POS.br}
`;

export const StickerRotateHandle = styled.div`
  position: absolute;
  top: -34px;
  left: 50%;
  transform: translateX(-50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #6366f1;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  cursor: grab;
  pointer-events: auto;
  touch-action: none;

  &::before {
    content: '';
    position: absolute;
    top: 18px;
    left: 50%;
    width: 2px;
    height: 16px;
    background: #6366f1;
    transform: translateX(-50%);
  }
`;

// Wraps the sticker content as a clickable anchor on the live site (read-only) when
// the author attached a link. Inert in edit mode (rendered only when !editable).
export const StickerLink = styled.a`
  display: block;
  width: 100%;
  text-decoration: none;
  color: inherit;
`;

// Action toolbar shown under a selected sticker (edit mode only). Counter-rotated so it
// stays upright regardless of the sticker's rotation.
export const StickerToolbar = styled.div`
  position: absolute;
  top: calc(100% + 14px);
  left: 50%;
  transform: translateX(-50%) rotate(${(props) => -(props.$rotation || 0)}deg);
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  background: #2b2b2b;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28);
  pointer-events: auto;
  z-index: 3;
`;

export const StickerToolButton = styled.button`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: ${(props) => (props.$active ? 'rgba(255,255,255,0.18)' : 'transparent')};
  color: ${(props) => (props.$danger ? '#ff6b6b' : '#fff')};
  cursor: pointer;
  touch-action: none;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  & svg {
    width: 17px;
    height: 17px;
  }
`;

export const LayersWrap = styled.div`
  position: relative;
  display: flex;
`;

export const StickerLayerMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.2);
  padding: 4px;
  min-width: 156px;
`;

export const StickerLayerMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #333;
  text-align: left;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: #f3f3f3;
  }
`;

export const StickerDelete = styled.div`
  position: absolute;
  top: -12px;
  right: -12px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ef4444;
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  pointer-events: auto;
  touch-action: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
`;
